import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { useQueryClient } from "react-query";

import { useStakeDebit } from "../../CustomHooks/Queries";
import { useVerifyPersonSynctera } from "../../CustomHooks/Mutations";

import useStakeCheckingStatusAction from "../../CustomHooks/useStakeCheckingStatusAction";

import { getStakeDebitAPIObject } from "../../Utils/api-data-transforms";
import {
  dispatchAPIErrorToast,
  getSyncteraSessionTokenFromStakeDebitResponse,
  getVerifyPersonSyncteraIdPayloadFromStakeDebitResponse,
  isBankMigrationVerificationRequiredFromStakeDebitResponse,
  isMobileDevice,
  BATTLECARD_FLOW_NAV_STATE,
  BATTLECARD_LEGACY_ONLY_STATE,
  isSVDDepositAccountCreated,
  isSVDPendingVerification,
  isSVDVerificationPendingStatus,
  isSVDVerificationRejected,
  shouldRouteHomeAfterDocvVerification,
} from "../../Utils/helpers";
import { contactStakeIfErrorPersistsMessage, isDevReactEnv, APIQueryKeys } from "../../Utils/constants";

import { StakeLogo } from "../../Assets/Images";
import { ArrowLeft, CloseWithShadow } from "../../Assets/Icons";

import "./verify-stake-checking.scss";

/** `/mobile/stake_debit/` can briefly return 500 right after apply / stake checking; wait before DocV + refetch. */
const STAKE_DEBIT_INITIAL_FETCH_DELAY_MS = 1500;
const SOCURE_SDK_URL = "https://websdk.socure.com/bundle.js";

function loadSocureDocvSdk() {
  if (window.SocureDocVSDK?.launch) {
    return Promise.resolve(window.SocureDocVSDK);
  }

  const existing = document.querySelector(`script[src="${SOCURE_SDK_URL}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      const onLoad = () => {
        cleanup();
        if (window.SocureDocVSDK?.launch) {
          resolve(window.SocureDocVSDK);
        } else {
          reject(new Error("Socure SDK unavailable"));
        }
      };
      const onError = () => {
        cleanup();
        reject(new Error("Socure SDK failed to load"));
      };
      const cleanup = () => {
        existing.removeEventListener("load", onLoad);
        existing.removeEventListener("error", onError);
      };
      existing.addEventListener("load", onLoad);
      existing.addEventListener("error", onError);
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SOCURE_SDK_URL;
    script.async = true;
    script.onload = () => {
      if (window.SocureDocVSDK?.launch) {
        resolve(window.SocureDocVSDK);
      } else {
        reject(new Error("Socure SDK unavailable"));
      }
    };
    script.onerror = () => reject(new Error("Socure SDK failed to load"));
    document.head.appendChild(script);
  });
}

let activeDocvLaunchId = 0;
let verifyPersonSubmitLock = false;

const resetDocvSession = () => {
  activeDocvLaunchId += 1;
};

const isActiveDocvLaunch = (launchId) => launchId === activeDocvLaunchId;

const onSyncteraVerifyError = () => {
  dispatchAPIErrorToast({
    key: "verifyStakeChecking",
    autoHideDuration: null,
  });
};

export default function VerifyStakeChecking() {
  const [isSDKLoaded, setIsSDKLoaded] = useState(false);
  const [isSDKLoadingError, setIsSDKLoadingError] = useState(false);
  const [stakeDebitFetchError, setStakeDebitFetchError] = useState(false);
  const [docvGateOpen, setDocvGateOpen] = useState(false);
  const hasInvokedLaunchRef = useRef(false);
  const stakeDebitRef = useRef(null);
  const navigateHomeTimeoutRef = useRef(null);

  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const queryClient = useQueryClient();

  const {
    isFetching: isFetchingStakeDebit,
    isError: isStakeDebitError,
    data: stakeDebit,
    refetch: refetchStakeDebit,
  } = useStakeDebit({
    retry(failureCount, error) {
      const status = error?.response?.status;
      if (status >= 500 && failureCount < 4) return true;
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
  });

  stakeDebitRef.current = stakeDebit;

  const stakeCheckingStatusAction = useStakeCheckingStatusAction();

  const { mutate: VerifyPersonSynctera } = useVerifyPersonSynctera({
    onSuccess: async ({ data }) => {
      try {
        const { data: refetchedStakeDebit } = await refetchStakeDebit();
        const stakeDebitBody = refetchedStakeDebit?.data;

        if (isSVDDepositAccountCreated(stakeDebitBody)) {
          navigate("/battlecard/add-funds", { replace: true, state: BATTLECARD_FLOW_NAV_STATE });
          return;
        }

        if (isSVDVerificationRejected(stakeDebitBody)) {
          navigate("/battlecard", {
            replace: true,
            state: { ...BATTLECARD_LEGACY_ONLY_STATE, ...BATTLECARD_FLOW_NAV_STATE },
          });
          return;
        }

        if (isSVDVerificationPendingStatus(stakeDebitBody)) {
          const previousPath = locationState?.previousPathname;
          if (previousPath?.startsWith("/battlecard")) {
            navigate("/battlecard/member-setup", { replace: true, state: BATTLECARD_FLOW_NAV_STATE });
          } else if (previousPath) {
            navigate(previousPath, { replace: true });
          } else {
            navigate(-1);
          }
          return;
        }

        if (shouldRouteHomeAfterDocvVerification(stakeDebitBody)) {
          navigate("/home", { replace: true });
          return;
        }

        stakeCheckingStatusAction(getStakeDebitAPIObject(data?.data?.stake_checking_status));
        if (navigateHomeTimeoutRef.current != null) {
          window.clearTimeout(navigateHomeTimeoutRef.current);
        }
        navigateHomeTimeoutRef.current = window.setTimeout(() => {
          navigateHomeTimeoutRef.current = null;
          navigate("/home", { replace: true });
        }, 5000);
      } catch (error) {
        verifyPersonSubmitLock = false;
        onSyncteraVerifyError();
      }
    },
    onError: () => {
      verifyPersonSubmitLock = false;
      onSyncteraVerifyError();
    },
  });

  const handleDocvSuccess = (launchId, docVTransactionToken) => {
    if (!isActiveDocvLaunch(launchId) || verifyPersonSubmitLock || !docVTransactionToken) {
      return;
    }
    verifyPersonSubmitLock = true;
    VerifyPersonSynctera({
      document_id: docVTransactionToken,
      ...getVerifyPersonSyncteraIdPayloadFromStakeDebitResponse(stakeDebitRef.current),
    });
  };

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDocvGateOpen(true);
      queryClient.invalidateQueries(APIQueryKeys.stakeDebit);
    }, STAKE_DEBIT_INITIAL_FETCH_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [queryClient]);

  useEffect(() => {
    if (!isStakeDebitError) {
      setStakeDebitFetchError(false);
    }
  }, [isStakeDebitError]);

  useEffect(() => {
    if (!docvGateOpen || isFetchingStakeDebit || isSDKLoaded || hasInvokedLaunchRef.current) {
      return;
    }

    if (isStakeDebitError) {
      setStakeDebitFetchError(true);
      return;
    }

    const shouldLaunchDocV =
      isSVDPendingVerification(stakeDebit?.data) ||
      isBankMigrationVerificationRequiredFromStakeDebitResponse(stakeDebit);

    const syncteraSessionToken = getSyncteraSessionTokenFromStakeDebitResponse(stakeDebit);

    if (!shouldLaunchDocV) {
      navigate(locationState?.previousPathname || -1);
      return;
    }

    if (!syncteraSessionToken) {
      setIsSDKLoadingError(true);
      return;
    }

    let cancelled = false;
    hasInvokedLaunchRef.current = true;
    resetDocvSession();
    const launchId = activeDocvLaunchId;

    loadSocureDocvSdk()
      .then((socureSdk) => {
        if (cancelled || !isActiveDocvLaunch(launchId)) return;

        const config = {
          onProgress: (e) => {
            if (!isActiveDocvLaunch(launchId)) return;
            if (isDevReactEnv) {
              console.log(e);
            }
          },
          onSuccess: ({ docVTransactionToken }) => {
            handleDocvSuccess(launchId, docVTransactionToken);
          },
          onError: () => {
            if (!isActiveDocvLaunch(launchId)) return;
            onSyncteraVerifyError();
          },
          qrCodeNeeded: !isMobileDevice(),
          disableSmsInput: false,
          closeCaptureWindowOnComplete: true,
          autoOpenTabOnMobile: false,
        };

        return socureSdk.launch(
          process.env.REACT_APP_SOCURE_SDK_KEY,
          syncteraSessionToken,
          "#socure-doc-v-sdk-container",
          config,
        );
      })
      .then((response) => {
        if (cancelled || !isActiveDocvLaunch(launchId)) return;
        if (response?.result === "error") {
          setIsSDKLoadingError(true);
        } else {
          setIsSDKLoaded(true);
        }
      })
      .catch(() => {
        if (cancelled || !isActiveDocvLaunch(launchId)) return;
        hasInvokedLaunchRef.current = false;
        setIsSDKLoadingError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [
    docvGateOpen,
    isFetchingStakeDebit,
    isSDKLoaded,
    isStakeDebitError,
    stakeDebit,
    locationState?.previousPathname,
    navigate,
  ]);

  useEffect(() => {
    verifyPersonSubmitLock = false;
    return () => {
      if (navigateHomeTimeoutRef.current != null) {
        window.clearTimeout(navigateHomeTimeoutRef.current);
      }
      resetDocvSession();
      verifyPersonSubmitLock = false;
    };
  }, []);

  const showStakeDebitLoading =
    !docvGateOpen || (isFetchingStakeDebit && !isSDKLoaded && !stakeDebitFetchError);

  return (
    <div className="authenticated-page verify-stake-checking-page">
      <header>
        <button className="back-button" onClick={() => navigate(locationState?.previousPathname || -1)} aria-label="Go back">
          <ArrowLeft />
        </button>
        <StakeLogo className="stake-logo" />
      </header>
      <div
        id="socure-doc-v-sdk-container"
        className={showStakeDebitLoading || isSDKLoadingError || stakeDebitFetchError ? "d-none" : ""}
      ></div>
      {(isSDKLoadingError || stakeDebitFetchError) && (
        <div className="error-content">
          <CloseWithShadow />
          <div className="error-description">{contactStakeIfErrorPersistsMessage}</div>
        </div>
      )}
      {showStakeDebitLoading && (
        <div className="loading-content">
          <CircularProgress />
        </div>
      )}
    </div>
  );
}